import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { getApiRoot } from "@ugg/shared/api/api-helpers";
import { useUGGApiVersions } from "@ugg/shared/api/requests/ugg-api-versions";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { RankS } from "@ugg/shared/utils/rank-helpers";

// Data Parsers
import { cleanObjectives } from "@ugg/shared/api/data-parser/tier-lists/objectives-tier-list";

// Interfaces
import { ObjectivesTierList } from "@ugg/shared/interfaces/tier-lists/objectives-tier-list.interface";

// ------------------------------------------------------------------------------------------------------------

interface TierListParams {
  region: string;
  patch: string;
  queueType: string;
  rank: string;
}

// ------------------------------------------------------------------------------------------------------------
// OBJECTIVES TIER LIST
// ------------------------------------------------------------------------------------------------------------

function useObjectivesURL(params: TierListParams) {
  const { data: version } = useUGGApiVersions({
    apiKey: "objectives",
    patch: params.patch,
  });

  if (
    params.queueType === QueueTypeS.ARAM ||
    params.queueType === QueueTypeS.URF ||
    params.queueType === QueueTypeS.ARURF ||
    params.queueType === QueueTypeS.NEXUS_BLITZ ||
    params.queueType === QueueTypeS.NORMAL_BLIND ||
    params.queueType === QueueTypeS.NORMAL_DRAFT ||
    params.queueType === QueueTypeS.QUICKPLAY ||
    params.queueType === QueueTypeS.SWIFTPLAY ||
    params.queueType === QueueTypeS.ONE_FOR_ALL
  ) {
    params.rank = RankS.OVERALL;
  }

  const root = getApiRoot(params.patch);
  return [`${root}/table/objective`, params.region, params.patch, params.queueType, params.rank, `${version}.json`].join("/");
}

export function useObjectivesTierList(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<ObjectivesTierList, ReturnType<typeof cleanObjectives>>,
) {
  const { params = {}, ssr = false, skip = false } = options || {};
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(TIER_LIST_PAGES.OBJECTIVES_TIER_LIST, params, true);

  const url = useObjectivesURL(validatedQueryParams);
  return useJSONFetcher<ObjectivesTierList, ReturnType<typeof cleanObjectives>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanObjectives(json),
  });
}
